import { useState } from "react";
import { useEnterAnimation } from "@/pages/services/hooks/useEnterAnimation";

/**
 * AboutTeam — the three therapists.
 *
 * Interaction: hovering a portrait lifts a dark caption strip up from the
 * bottom of the image revealing that therapist's specialties and a short
 * bio. On mobile, tap toggles the same reveal.
 *
 * Image discipline: 3 portrait images all generated with a consistent
 * warm-cream editorial style so they read as a series.
 */

interface Therapist {
  name: string;
  title: string;
  credentials: string;
  specialties: string[];
  bio: string;
  image: string;
}

const TEAM: Therapist[] = [
  {
    name: "Marta Ribeiro",
    title: "Founder · Lead Therapist",
    credentials: "MSc Clinical Psychology, OPP #12481",
    specialties: ["Anxiety", "Burnout", "Grief"],
    bio:
      "Marta founded Serene in 2016. She works most often with adults navigating burnout and quiet, long-held grief.",
    image:
      "https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg?auto=compress&cs=tinysrgb&w=1200&h=1500&fit=crop",
  },
  {
    name: "João Almeida",
    title: "Couples Therapist",
    credentials: "MSc Family Therapy, OPP #18730",
    specialties: ["Couples", "Communication", "Attachment"],
    bio:
      "João holds the couples work at Serene. His approach is quiet and structural &mdash; he watches for the pattern between two people, not the words.",
    image:
      "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=1200&h=1500&fit=crop",
  },
  {
    name: "Sofia Neves",
    title: "Trauma & Somatic Therapist",
    credentials: "MSc, EMDR-certified, OPP #21044",
    specialties: ["Trauma", "Somatic", "Anxiety"],
    bio:
      "Sofia integrates EMDR and somatic work for clients healing from trauma. She believes bodies remember things words haven't found yet.",
    image:
      "https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=1200&h=1500&fit=crop",
  },
];

export default function AboutTeam() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const eyebrowRef = useEnterAnimation<HTMLDivElement>({ delay: 0 });
  const titleRef = useEnterAnimation<HTMLHeadingElement>({ delay: 140 });
  const gridRef = useEnterAnimation<HTMLDivElement>({ delay: 300 });

  return (
    <section
      id="about-team"
      className="relative w-full bg-background-100"
      style={{ minHeight: "100vh" }}
    >
      {/* Section meta */}
      <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-14 md:pt-16 pointer-events-none">
        <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
            About Us / 04
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
            Chapter 04 · The people
          </span>
        </div>
      </div>

      <div className="w-full min-h-screen flex flex-col items-center justify-center px-6 md:px-10 py-28 md:py-36">
        <div className="max-w-[1400px] w-full mx-auto">
          <div ref={eyebrowRef} className="mb-8 flex items-center gap-3">
            <span
              className="font-heading"
              style={{
                fontSize: "clamp(2.75rem, 4vw, 4rem)",
                fontWeight: 300,
                letterSpacing: "-0.03em",
                color: "oklch(var(--foreground-950) / 0.35)",
                lineHeight: 1,
                fontVariationSettings: "'opsz' 144",
              }}
            >
              04
            </span>
            <span className="w-12 h-[1px] bg-foreground-950/25"></span>
            <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
              Our therapists
            </span>
          </div>

          <h2
            ref={titleRef}
            className="mb-16 max-w-[900px]"
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "clamp(2.5rem, 5vw, 5rem)",
              fontWeight: 300,
              letterSpacing: "-0.025em",
              lineHeight: 1.05,
              color: "oklch(var(--foreground-950))",
              fontVariationSettings: "'opsz' 144",
            }}
          >
            Three people who&rsquo;ll be
            <br />
            <span className="italic text-foreground-950/85">
              in the room with you.
            </span>
          </h2>

          <div
            ref={gridRef}
            className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-7"
          >
            {TEAM.map((person, i) => {
              const isActive = activeIndex === i;
              return (
                <div
                  key={person.name}
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseLeave={() => setActiveIndex(null)}
                  onFocus={() => setActiveIndex(i)}
                  onBlur={() => setActiveIndex(null)}
                  onClick={() =>
                    setActiveIndex(isActive ? null : i)
                  }
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setActiveIndex(isActive ? null : i);
                    }
                  }}
                  className="relative w-full overflow-hidden rounded-2xl cursor-pointer group focus:outline-none"
                  style={{ aspectRatio: "3 / 4" }}
                >
                  <img
                    src={person.image}
                    alt={`${person.name} — ${person.title}`}
                    title={person.name}
                    className="absolute inset-0 w-full h-full object-cover object-center select-none transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                    draggable={false}
                  />

                  {/* Static bottom gradient (always present, softer) */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 transition-opacity duration-500"
                    style={{
                      height: "45%",
                      background:
                        "linear-gradient(to top, rgba(20,15,10,0.65) 0%, rgba(20,15,10,0.28) 55%, rgba(20,15,10,0) 100%)",
                      opacity: isActive ? 0 : 1,
                    }}
                  />

                  {/* Static name card (small, bottom) */}
                  <div
                    className="absolute bottom-6 left-6 right-6 transition-all duration-500"
                    style={{
                      opacity: isActive ? 0 : 1,
                      transform: isActive
                        ? "translate3d(0, 20px, 0)"
                        : "translate3d(0, 0, 0)",
                    }}
                  >
                    <div className="flex items-center gap-2.5 mb-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                      <span
                        className="uppercase font-medium"
                        style={{
                          fontSize: "10px",
                          letterSpacing: "0.3em",
                          color: "rgba(255,255,255,0.72)",
                        }}
                      >
                        {person.title}
                      </span>
                    </div>
                    <div
                      style={{
                        fontFamily: "var(--font-heading)",
                        fontSize: "clamp(1.4rem, 1.8vw, 1.8rem)",
                        fontWeight: 400,
                        letterSpacing: "-0.015em",
                        lineHeight: 1.1,
                        color: "#FFFFFF",
                      }}
                    >
                      {person.name}
                    </div>
                  </div>

                  {/* Detail reveal — sits on top when active */}
                  <div
                    className="absolute inset-0 flex flex-col justify-end p-6 md:p-7 transition-opacity duration-500"
                    style={{
                      background:
                        "linear-gradient(to top, rgba(20,15,10,0.9) 0%, rgba(20,15,10,0.72) 45%, rgba(20,15,10,0) 100%)",
                      opacity: isActive ? 1 : 0,
                      pointerEvents: isActive ? "auto" : "none",
                    }}
                  >
                    <div className="flex items-center gap-2.5 mb-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                      <span
                        className="uppercase font-medium"
                        style={{
                          fontSize: "10px",
                          letterSpacing: "0.3em",
                          color: "rgba(255,255,255,0.72)",
                        }}
                      >
                        {person.title}
                      </span>
                    </div>
                    <div
                      className="mb-1"
                      style={{
                        fontFamily: "var(--font-heading)",
                        fontSize: "clamp(1.5rem, 2vw, 2rem)",
                        fontWeight: 400,
                        letterSpacing: "-0.015em",
                        lineHeight: 1.05,
                        color: "#FFFFFF",
                      }}
                    >
                      {person.name}
                    </div>
                    <div
                      className="mb-4"
                      style={{
                        fontFamily: "var(--font-body)",
                        fontSize: "12px",
                        letterSpacing: "0.02em",
                        color: "rgba(255,255,255,0.6)",
                      }}
                    >
                      {person.credentials}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      {person.specialties.map((s) => (
                        <span
                          key={s}
                          className="inline-flex items-center rounded-full border px-3 py-1"
                          style={{
                            fontSize: "10px",
                            letterSpacing: "0.25em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.82)",
                            borderColor: "rgba(255,255,255,0.3)",
                          }}
                        >
                          {s}
                        </span>
                      ))}
                    </div>

                    <p
                      className="italic max-w-[360px]"
                      style={{
                        fontFamily: "var(--font-heading)",
                        fontSize: "clamp(0.9rem, 1vw, 1.05rem)",
                        fontWeight: 300,
                        lineHeight: 1.55,
                        color: "rgba(255,255,255,0.88)",
                        letterSpacing: "0.005em",
                      }}
                      dangerouslySetInnerHTML={{ __html: person.bio }}
                    ></p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}